import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Factory, CheckCircle, Truck, Shield, Clock, ArrowRight, X } from 'lucide-react';

// Factory images from user-provided assets
const FACTORY_IMAGES = {
  packing: "https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/w54p034i_image.png",
  production: "https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/xc04hkqg_Factory%201.png",
  facility: "https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/7lo3zof8_Factory%202.png"
};

export function ProductionUnitSection() {
  const [activeImage, setActiveImage] = useState(0);
  const [showGallery, setShowGallery] = useState(false);

  const images = [
    { src: FACTORY_IMAGES.production, label: "Production Floor", description: "German-standard edge banding & assembly" },
    { src: FACTORY_IMAGES.facility, label: "State-of-Art Facility", description: "Modern machinery & quality control" },
    { src: FACTORY_IMAGES.packing, label: "Quality Packing", description: "Secure packaging for safe delivery" }
  ];

  const trustPoints = [
    { icon: Factory, text: "Own Manufacturing Unit", detail: "Not a reseller" },
    { icon: Shield, text: "German-Standard Technology", detail: "Precision engineering" },
    { icon: Clock, text: "Rapid Scalability", detail: "Bulk orders ready" },
    { icon: Truck, text: "Direct from Factory", detail: "No middleman markup" }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white" data-testid="production-unit-section">
      <div className="container mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-bold mb-4">
            <Factory className="w-4 h-4" />
            OUR PRODUCTION FACILITY
          </div>
          <h2 className="text-3xl md:text-5xl font-black uppercase mb-4">
            From Precision Engineering<br />
            <span className="text-green-600">To Your Personal Space</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Where German-standard technology meets artisanal care
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div 
              className="relative overflow-hidden rounded-lg border-4 border-green-500 shadow-2xl cursor-pointer group"
              onClick={() => setShowGallery(true)}
            >
              <img
                src={images[activeImage].src}
                alt={images[activeImage].label}
                className="w-full h-[300px] md:h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white font-bold text-xl">{images[activeImage].label}</p>
                <p className="text-white/80 text-sm">{images[activeImage].description}</p>
              </div>
              <div className="absolute top-4 right-4 bg-white/90 px-3 py-1 rounded-full text-xs font-bold text-green-700">
                Click to expand
              </div>
            </div>

            {/* Thumbnail Navigation */}
            <div className="flex gap-3">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImage(idx)}
                  className={`flex-1 rounded-lg overflow-hidden border-2 transition-all ${
                    activeImage === idx 
                      ? 'border-green-500 ring-2 ring-green-300' 
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <img
                    src={img.src}
                    alt={img.label}
                    className="w-full h-16 md:h-20 object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {/* Main Copy */}
            <div className="bg-white p-6 md:p-8 rounded-xl border-2 border-gray-100 shadow-lg">
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
                At <strong>Modular Furniture Store (MFS)</strong>, we don't just sell furniture - we manufacture solutions. 
                Our state-of-the-art production facility is the heart of every piece that enters your home or office.
              </p>

              {/* B2C Pitch */}
              <div className="bg-green-50 p-4 rounded-lg mb-4 border-l-4 border-green-500">
                <p className="font-bold text-green-800 mb-1">For the Homeowner (B2C)</p>
                <p className="text-gray-700 text-sm">
                  Factory-direct quality without middleman markup. Every edge is banded with precision, 
                  every panel inspected by our MFS-certified team to ensure your home stays beautiful for years.
                </p>
              </div>

              {/* B2B Pitch */}
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <p className="font-bold text-blue-800 mb-1">For the Professional (B2B)</p>
                <p className="text-gray-700 text-sm">
                  Rapid scalability, consistent bulk quality, and technical precision for complex modular layouts. 
                  From single offices to 20-floor corporate setups, we deliver on time, every time.
                </p>
              </div>
            </div>

            {/* Trust Points Grid */}
            <div className="grid grid-cols-2 gap-3">
              {trustPoints.map((point, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-3 bg-white p-3 rounded-lg border border-gray-200 hover:border-green-300 hover:shadow-md transition-all"
                >
                  <div className="p-2 bg-green-100 rounded-lg">
                    <point.icon className="w-5 h-5 text-green-700" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-gray-900">{point.text}</p>
                    <p className="text-xs text-gray-500">{point.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA - Our Story */}
            <Link 
              to="/about" 
              className="inline-flex items-center gap-2 text-gray-700 hover:text-green-700 transition-colors group"
              onClick={() => window.scrollTo(0, 0)}
            >
              <span className="text-sm font-medium">Our Story</span>
              <span className="text-xs text-gray-500 italic">Crafting Workspaces with Legacy and Purpose</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Bottom Trust Banner */}
        <div className="mt-12 bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-6 md:p-8 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-full">
                <CheckCircle className="w-8 h-8" />
              </div>
              <div>
                <p className="text-xl md:text-2xl font-black">We Own Our Manufacturing</p>
                <p className="text-green-100 text-sm">Not a reseller. Not a dropshipper. 100% factory-direct quality.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <img src="https://flagcdn.com/w40/in.png" alt="India" className="w-6 h-4 rounded" />
              <span className="font-bold">Made in India</span>
            </div>
          </div>
        </div>
      </div>

      {/* Full Screen Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
          <button 
            onClick={() => setShowGallery(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-5xl w-full">
            <img
              src={images[activeImage].src}
              alt={images[activeImage].label}
              className="w-full max-h-[70vh] object-contain rounded-lg"
            />
            <div className="flex justify-center gap-4 mt-6">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImage(idx)}
                  className={`rounded-lg overflow-hidden border-2 transition-all ${
                    activeImage === idx ? 'border-green-500' : 'border-white/30'
                  }`}
                >
                  <img src={img.src} alt={img.label} className="w-24 h-16 object-cover" />
                </button>
              ))}
            </div>
            <p className="text-center text-white mt-4 text-xl font-bold">{images[activeImage].label}</p>
          </div>
        </div>
      )}
    </section>
  );
}

// Compact Trust Badge for Product Pages - placed near Add to Cart
export function FactoryTrustBadge() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="w-full flex items-center gap-3 bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-200 hover:border-green-400 rounded-lg p-3 transition-all group mt-4"
        data-testid="factory-trust-badge"
      >
        <div className="p-2 bg-green-600 rounded-lg">
          <Factory className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-bold text-green-800 text-sm">Factory Direct Price</p>
          <p className="text-xs text-green-600">View our production facility</p>
        </div>
        <ArrowRight className="w-4 h-4 text-green-600 group-hover:translate-x-1 transition-transform" />
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h3 className="text-xl font-black uppercase flex items-center gap-2">
                <Factory className="w-5 h-5 text-green-600" />
                Our Production Facility
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Image Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <img
                  src={FACTORY_IMAGES.production}
                  alt="Production Floor"
                  className="w-full h-40 object-cover rounded-lg col-span-2"
                />
                <img
                  src={FACTORY_IMAGES.facility}
                  alt="Facility"
                  className="w-full h-32 object-cover rounded-lg"
                />
                <img
                  src={FACTORY_IMAGES.packing}
                  alt="Packing"
                  className="w-full h-32 object-cover rounded-lg"
                />
              </div>

              {/* Content */}
              <div className="space-y-4">
                <h4 className="font-bold text-lg">From Precision Engineering to Your Personal Space</h4>
                <p className="text-gray-600 text-sm">
                  At MFS, we don't just sell furniture - we manufacture solutions. Our state-of-the-art 
                  production facility uses German-standard technology with artisanal care.
                </p>

                {/* Key Points */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon: Factory, text: "Own Manufacturing" },
                    { icon: Shield, text: "Quality Certified" },
                    { icon: Truck, text: "Factory Direct" },
                    { icon: Clock, text: "Quick Delivery" }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-green-50 p-2 rounded-lg">
                      <item.icon className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <Link to="/bulk-inquiry" onClick={() => { setShowModal(false); window.scrollTo(0, 0); }}>
                <Button className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white font-bold py-5">
                  Get Factory Direct Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProductionUnitSection;
